import { Card, Icon, useRelativeDate } from '~/ui-components';
import { Text, Flex, CircularProgress, LinkBox, LinkOverlay } from '@chakra-ui/react';
import { useLocalization } from '~/framework';
import { faPlay, faCheckCircle } from '@fortawesome/pro-regular-svg-icons';
import Link from 'next/link';
import { getUnixTime } from 'date-fns';
import { IndividualSummaryDetailCourse } from './useIndividualStageSummaryDetails';

type CourseCardProps = {
   course: IndividualSummaryDetailCourse;
};

type Keys = 'statusColor' | 'buttonText' | 'textColor';

type CompletionStatusOptions = {
   [key: string]: Record<Keys, string>;
};

export function CourseCard({ course }: CourseCardProps) {
   const { _js, ___p } = useLocalization();
   const relativeDateString = useRelativeDate(course.dueDate);

   const completionStatusOptions: CompletionStatusOptions = {
      pending: {
         statusColor: 'gray.100',
         buttonText: _js('Start'),
         textColor: 'gray.400',
      },
      started: {
         statusColor: 'blue.500',
         buttonText: _js('Resume'),
         textColor: 'white',
      },
      completed: {
         statusColor: 'green.500',
         buttonText: _js('Review'),
         textColor: 'white',
      },
      expired: {
         statusColor: 'red.500',
         buttonText: _js('Resume'),
         textColor: 'white',
      },
      invalidated: {
         statusColor: 'yellow.500',
         buttonText: _js('Resume'),
         textColor: 'white',
      },
   };

   let status = '';

   if (course.dueDate !== null && course.dueDate < getUnixTime(Date.now())) {
      status = `${_js('Expired')} ${relativeDateString}`;
   }

   let courseCompletionLabel: string;

   if (course.completionStatus === 'completed') {
      courseCompletionLabel = _js('Done');
   } else if (course.completionStatus === 'pending') {
      courseCompletionLabel = _js('Not Started');
   } else {
      courseCompletionLabel = `${course.completionPercent}%`;
   }

   return (
      <Card maxW={{ md: '48.5%', lg: '31.5%' }} mb="6" minH="18rem">
         <LinkBox display="flex" flexDirection="column" w="100%">
            <Flex
               minHeight="3.5rem"
               px="4"
               alignItems="center"
               justify="space-between"
               backgroundColor={completionStatusOptions[course.completionStatus].statusColor}
               borderTopRightRadius="lg"
               borderTopLeftRadius="lg"
            >
               <Flex alignItems="center">
                  {course.completionStatus === 'completed' ? (
                     <Icon type={faCheckCircle} fontSize="25px" color="white" />
                  ) : (
                     <CircularProgress
                        opacity={course.completionStatus === 'pending' ? '0.4' : '1'}
                        size="25px"
                        thickness="14px"
                        trackColor="rgba(36,44,51,0.4)"
                        value={course.completionPercent}
                        color="white"
                     />
                  )}

                  <Text
                     ml="2"
                     fontSize="sm"
                     variant="title"
                     color={completionStatusOptions[course.completionStatus].textColor}
                  >
                     {courseCompletionLabel}
                  </Text>
               </Flex>
               <Text
                  fontSize="sm"
                  variant="title"
                  color={completionStatusOptions[course.completionStatus].textColor}
               >
                  {___p(course.total, '%1 Stage', '%1 Stages', `${course.total}`)}
               </Text>
            </Flex>

            <Flex direction="column" justify="space-between" h="100%" mt="6" px="6">
               <Flex direction="column">
                  {status && course.completionStatus !== 'completed' && (
                     <Text fontSize="xs" variant="title" color="red.500" aria-label={status}>
                        {status}
                     </Text>
                  )}
                  <Text
                     fontSize="lg"
                     variant={course.completionStatus === 'pending' ? 'muted' : 'title'}
                     mt="1"
                     noOfLines={3}
                  >
                     {course.title}
                  </Text>
                  <Text fontSize="sm" variant="muted" mt="1" noOfLines={3}>
                     {course.description}
                  </Text>
               </Flex>
               <Link href={course.assignmentUrl} passHref legacyBehavior>
                  <LinkOverlay>
                     <Flex alignItems="center" my="6" cursor="pointer">
                        <Text
                           fontSize="sm"
                           variant="title"
                           py="1.5"
                           px="4"
                           backgroundColor="gray.50"
                           borderLeftRadius="5%"
                        >
                           {completionStatusOptions[course.completionStatus].buttonText}
                        </Text>
                        <Icon
                           type={faPlay}
                           py="7px"
                           px="2"
                           color="blueGray.600"
                           backgroundColor="gray.100"
                           borderRightRadius="5%"
                        />
                     </Flex>
                  </LinkOverlay>
               </Link>
            </Flex>
         </LinkBox>
      </Card>
   );
}
